import React, { useRef, useState } from 'react';
import { Building2, Upload, Trash2, Loader2, AlertCircle, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useOrganization } from '../../hooks/useOrganization';
import { useOrganizationLogo } from '../../hooks/useOrganizationLogo';
import { useIsAdmin } from '@/hooks/useUserRole';
import { FetchOrganizationLogoModal } from './FetchOrganizationLogoModal';

export interface OrganizationLogoUploaderProps {
  className?: string;
  compact?: boolean;
}

export const OrganizationLogoUploader: React.FC<OrganizationLogoUploaderProps> = ({
  className = '',
  compact = false,
}) => {
  const { currentOrganization } = useOrganization();
  const isAdmin = useIsAdmin();
  const { uploadLogo, removeLogo, isUploading, isRemoving } = useOrganizationLogo();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageError, setImageError] = useState(false);
  const [fetchModalOpen, setFetchModalOpen] = useState(false);

  const logoUrl = currentOrganization?.logo_url;
  const isBusy = isUploading || isRemoving;

  React.useEffect(() => {
    setImageError(false);
  }, [logoUrl]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadLogo(file);
    } catch {
      // Toast notifications are already dispatched by the hook
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Are you sure you want to remove the organization logo?')) {
      return;
    }

    try {
      await removeLogo();
    } catch {
      // Toast notifications are dispatched by the hook
    }
  };

  if (compact) {
    return (
      <div className={`space-y-1.5 max-w-[520px] ${className}`}>
        <Label className="text-xs sm:text-sm font-semibold text-gray-900">
          Organization Logo
        </Label>
        <div className="flex items-center justify-between p-2 rounded-xl border border-gray-200/80 bg-white shadow-none">
          <div className="flex items-center gap-3">
            <div className="relative w-11 h-11 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
              {logoUrl && !imageError ? (
                <img
                  src={logoUrl}
                  alt={currentOrganization?.name || 'Organization Logo'}
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-full h-full bg-[#F5EEFE] flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#9333EA]" />
                </div>
              )}
              {isBusy && (
                <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-900 truncate max-w-[140px] sm:max-w-[200px]">
                {logoUrl ? (currentOrganization?.name || 'Custom Logo') : 'No logo uploaded'}
              </p>
              <p className="text-[11px] text-gray-400">
                PNG, JPG, WEBP, SVG (max 5MB)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={handleFileChange}
              className="hidden"
              disabled={!isAdmin || isBusy}
            />

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={!isAdmin || isBusy}
              className="h-8 px-2.5 text-xs gap-1.5 border-purple-200 text-purple-700 bg-white hover:bg-white hover:text-purple-700 transition-none"
            >
              {isUploading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-600" />
              ) : (
                <Upload className="w-3.5 h-3.5 text-purple-600" />
              )}
              {logoUrl ? 'Change' : 'Upload'}
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFetchModalOpen(true)}
              disabled={!isAdmin || isBusy}
              className="h-8 px-2.5 text-xs gap-1.5 border-purple-200 text-purple-700 bg-white hover:bg-white hover:text-purple-700 transition-none"
            >
              <Globe className="w-3.5 h-3.5 text-purple-600" />
              Fetch
            </Button>

            {logoUrl && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemove}
                disabled={!isAdmin || isBusy}
                className="h-8 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                title="Remove logo"
              >
                {isRemoving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
              </Button>
            )}
          </div>
        </div>

        {!isAdmin && (
          <p className="text-[11px] text-amber-600">
            Only organization admins can modify the logo.
          </p>
        )}

        <FetchOrganizationLogoModal
          open={fetchModalOpen}
          onOpenChange={setFetchModalOpen}
        />
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl border border-gray-200/80 p-6 shadow-sm space-y-4 ${className}`}>
      <div>
        <h3 className="text-base font-semibold text-gray-900">Organization Logo</h3>
        <p className="text-xs text-gray-500 mt-1">
          Upload directly from your device or fetch automatically from your website. Formats: PNG, JPEG, WEBP, SVG (max 5MB).
        </p>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-6 pt-2">
        {/* Preview Frame */}
        <div className="relative w-20 h-20 rounded-2xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
          {logoUrl && !imageError ? (
            <img
              src={logoUrl}
              alt={currentOrganization?.name || 'Organization Logo'}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full bg-[#A655F8]/10 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-[#A655F8]" />
            </div>
          )}

          {isBusy && (
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>

        {/* Action Controls */}
        <div className="flex flex-col gap-2 w-full sm:w-auto">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={handleFileChange}
            className="hidden"
            disabled={!isAdmin || isBusy}
          />

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={!isAdmin || isBusy}
              className="gap-2 border-purple-200 text-purple-700 bg-white hover:bg-white hover:text-purple-700 transition-none"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 text-purple-600" />
                  {logoUrl ? 'Change Logo' : 'Upload from Device'}
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFetchModalOpen(true)}
              disabled={!isAdmin || isBusy}
              className="gap-2 border-purple-200 text-purple-700 bg-white hover:bg-white hover:text-purple-700 transition-none"
            >
              <Globe className="w-4 h-4 text-purple-600" />
              Fetch Logo
            </Button>

            {logoUrl && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemove}
                disabled={!isAdmin || isBusy}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-2"
              >
                {isRemoving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </>
                )}
              </Button>
            )}
          </div>

          {!isAdmin && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 mt-1">
              <AlertCircle className="w-3.5 h-3.5" />
              Only organization admins can modify the logo.
            </p>
          )}
        </div>
      </div>

      <FetchOrganizationLogoModal
        open={fetchModalOpen}
        onOpenChange={setFetchModalOpen}
      />
    </div>
  );
};

export default OrganizationLogoUploader;
